import {
	Id,
	LaravelResponse,
	PaginatedListFetcherConfig,
	keyToUrl,
	mapPaginatedFetcherConfigToAxiosConfig,
	withDestroyEndpoint,
	withShowEndpoint,
	withStoreEndpoint,
	withUpdateEndpoint,
} from "@dgs/core";
import axios from "axios";
import { BulkRequestWithFilterId } from "../../../../shared/types/bulkRequestWithFilterId";
import { IContactOptionResource } from "../types";
import {
	AcceptDeletionInquiriesBulkRequest,
	ContactUpdateInquiryCompletedIndexResource,
	ContactUpdateInquiryContactDiffResource,
	ContactUpdateInquiryContactIndexResource,
	ContactUpdateInquiryDetailsResource,
	ContactUpdateInquiryIndexResource,
	ContactUpdateInquirySaveRequest,
	ContactUpdateInquiryShowResource,
	ContactUpdateSaveRequest,
} from "../types/contactUpdateInquiry";

const baseKey = ["contact-update-inquiries"];
const detailsKey = (id: Id) => [...baseKey, `${id}`, "details"];

const contactsWithInquiryKeySuffix = "contacts-with-inquiry";
const contactsWithOpenUpdateInquiryKey = (id: Id) => [...baseKey, `${id}`, "update-inquiries"];
const contactsWithOpenDeletionInquiryKey = (id: Id) => [...baseKey, `${id}`, "deletion-inquiries"];
const contactsWithCompletedInquiryKey = (id: Id) => [...baseKey, `${id}`, "completed-inquiries"];
const bulkAcceptDeletionInquiriesKey = (id: Id) => contactsWithOpenDeletionInquiryKey(id);
const acceptDeletionInquiryKey = (inquiryId: Id, contactId: Id) => [
	...contactsWithOpenDeletionInquiryKey(inquiryId),
	`${contactId}`,
];

const contactUpdateKey = (inquiryId: Id, contactId: Id) => [
	...contactsWithOpenUpdateInquiryKey(inquiryId),
	`${contactId}`,
];

const contactDiffKey = (inquiryId: Id, contactId: Id) => [
	...contactsWithOpenUpdateInquiryKey(inquiryId),
	`${contactId}`,
];

const acceptDeletionInquiry = (inquiryId: Id, contactId: Id, acceptInquiry: boolean) => {
	return axios.put<LaravelResponse<void>>(keyToUrl(acceptDeletionInquiryKey(inquiryId, contactId)), { acceptInquiry });
};

const bulkAcceptDeletionInquiries = (inquiryId: Id, request: AcceptDeletionInquiriesBulkRequest) =>
	axios.put<LaravelResponse<void>>(keyToUrl(bulkAcceptDeletionInquiriesKey(inquiryId)), request);

const contactsWithOpenUpdateInquiry = (
	config: PaginatedListFetcherConfig<ContactUpdateInquiryContactIndexResource>,
	contactFilterId: Id | null,
	contactViewId: Id | null,
	contactUpdateInquiryId: Id,
) => {
	return contactWithInquiry(
		config,
		contactFilterId,
		contactViewId,
		keyToUrl(contactsWithOpenUpdateInquiryKey(contactUpdateInquiryId)),
	);
};

const contactsWithOpenDeletionInquiry = (
	config: PaginatedListFetcherConfig<ContactUpdateInquiryContactIndexResource>,
	contactFilterId: Id | null,
	contactViewId: Id | null,
	contactUpdateInquiryId: Id,
) => {
	return contactWithInquiry(
		config,
		contactFilterId,
		contactViewId,
		keyToUrl(contactsWithOpenDeletionInquiryKey(contactUpdateInquiryId)),
	);
};

const contactsWithCompletedInquiry = (
	config: PaginatedListFetcherConfig<ContactUpdateInquiryCompletedIndexResource>,
	contactFilterId: Id | null,
	contactViewId: Id | null,
	contactUpdateInquiryId: Id,
) => {
	const axiosConfig = mapPaginatedFetcherConfigToAxiosConfig(config);

	return axios.get<LaravelResponse<ContactUpdateInquiryCompletedIndexResource[]>>(
		keyToUrl(contactsWithCompletedInquiryKey(contactUpdateInquiryId)),
		{
			...axiosConfig,
			params: {
				...axiosConfig.params,
				sort: config.sortParams
					? {
							contactAttributeId: config.sortParams.column,
							direction: config.sortParams.direction,
						}
					: null,
				contactFilterId,
				contactViewId,
			},
		},
	);
};

const contactWithInquiry = (
	config: PaginatedListFetcherConfig<ContactUpdateInquiryContactIndexResource>,
	contactFilterId: Id | null,
	contactViewId: Id | null,
	url: string,
) => {
	const axiosConfig = mapPaginatedFetcherConfigToAxiosConfig(config);

	return axios.get<LaravelResponse<ContactUpdateInquiryContactIndexResource[]>>(url, {
		...axiosConfig,
		params: {
			...axiosConfig.params,
			sort: config.sortParams
				? {
						contactAttributeId: config.sortParams.column,
						direction: config.sortParams.direction,
					}
				: null,
			contactFilterId,
			contactViewId,
		},
	});
};
const contactsWithPendingFeedbackKey = (id: Id) => [...baseKey, `${id}`, "pending-feedback"];

const contactsWithPendingFeedback = (
	config: PaginatedListFetcherConfig<ContactUpdateInquiryContactIndexResource>,
	contactFilterId: Id | null,
	contactViewId: Id | null,
	contactUpdateInquiryId: Id,
) => {
	const axiosConfig = mapPaginatedFetcherConfigToAxiosConfig(config);

	return axios.get<LaravelResponse<ContactUpdateInquiryContactIndexResource[]>>(
		keyToUrl(contactsWithPendingFeedbackKey(contactUpdateInquiryId)),
		{
			...axiosConfig,
			params: {
				...axiosConfig.params,
				sort: config.sortParams
					? {
							contactAttributeId: config.sortParams.column,
							direction: config.sortParams.direction,
						}
					: null,
				contactFilterId,
				contactViewId,
			},
		},
	);
};
const resendContactUpdateEmailKey = (id: Id) => [...baseKey, `${id}`, "resend"];

const resendContactUpdateEmail = (inquiryId: Id, request: BulkRequestWithFilterId) =>
	axios.put<LaravelResponse<void>>(keyToUrl(resendContactUpdateEmailKey(inquiryId)), request);
const closeInquiriesForContactKey = (contactUpdateInquiryId: Id) => [...baseKey, `${contactUpdateInquiryId}`, "close"];

const closeInquiriesForContact = (inquiryId: Id, request: BulkRequestWithFilterId) =>
	axios.put<LaravelResponse<void>>(keyToUrl(closeInquiriesForContactKey(inquiryId)), request);

const contactsWithConflictsKey = (contactUpdateInquiryId: Id) => [...baseKey, `${contactUpdateInquiryId}`, "conflicts"];

export const contactUpdateInquiryService = {
	index: (config: PaginatedListFetcherConfig<ContactUpdateInquiryIndexResource>) =>
		axios.get<LaravelResponse<ContactUpdateInquiryIndexResource[]>>(keyToUrl(baseKey), {
			params: { ...mapPaginatedFetcherConfigToAxiosConfig(config).params, orderBy: config.sortParams },
		}),
	listKey: [...baseKey, "list"],
	indexKey: baseKey,
	...withShowEndpoint<ContactUpdateInquiryShowResource>(baseKey),
	...withStoreEndpoint<ContactUpdateInquirySaveRequest, { id: Id }>(baseKey),
	...withUpdateEndpoint<ContactUpdateInquirySaveRequest>(baseKey),
	...withDestroyEndpoint(baseKey),
	detailsKey,
	details: (id: Id) => axios.get<LaravelResponse<ContactUpdateInquiryDetailsResource>>(keyToUrl(detailsKey(id))),
	contactsWithInquiryKeySuffix,
	contactsWithOpenUpdateInquiry,
	contactsWithOpenUpdateInquiryKey,
	contactsWithOpenDeletionInquiry,
	contactsWithOpenDeletionInquiryKey,
	acceptDeletionInquiryKey,
	acceptDeletionInquiry,
	bulkAcceptDeletionInquiriesKey,
	bulkAcceptDeletionInquiries,
	contactDiffKey,
	contactDiff: (inquiryId: Id, contactId: Id) =>
		axios.get<LaravelResponse<ContactUpdateInquiryContactDiffResource>>(keyToUrl(contactDiffKey(inquiryId, contactId))),
	contactUpdateKey,
	contactUpdate: (inquiryId: Id, contactId: Id, request: ContactUpdateSaveRequest) =>
		axios.put(keyToUrl(contactUpdateKey(inquiryId, contactId)), request),
	contactsWithCompletedInquiry,
	contactsWithCompletedInquiryKey,
	contactsWithPendingFeedbackKey,
	contactsWithPendingFeedback,
	resendContactUpdateEmail,
	resendContactUpdateEmailKey,
	closeInquiriesForContact,
	closeInquiriesForContactKey,
	contactsWithConflictsKey,
	contactsWithConflicts: (contactUpdateInquiryId: Id) =>
		axios.get<LaravelResponse<IContactOptionResource[]>>(keyToUrl(contactsWithConflictsKey(contactUpdateInquiryId))),
};
